<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { solutionPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'SolutionName',

  props: {
    solution: {
      type: Object as PropType<Solution>,
      required: true,
    },

    /**
     * リンクにするかどうか
     */
    hasLink: {
      type: Boolean,
      default: true,
    },

    /**
     * フォントサイズ
     *  small ≒ 12px
     *  normal ≒ 16px
     */
    size: {
      type: String as PropType<'small' | 'normal'>,
      default: 'small',
      validator: (value: string) => {
        return ['small', 'normal'].includes(value)
      },
    },

    /**
     * 幅超過の際に末尾を「…」で省略するかどうか
     */
    isTruncate: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const wrapperTagName = computed(() => props.solution.id && props.hasLink ? 'a' : 'p')

    return {
      // routes
      solutionPath,

      // computed
      wrapperTagName,
    }
  },
})
</script>

<template>
  <div
    :class="[
      'o-label__name',
      `is-${size}`,
      { 'is-truncate': isTruncate },
    ]">
    <component
      :is="wrapperTagName"
      class="o-label__name--item"
      :href="hasLink && solution.id ? solutionPath({id: solution.id}) : undefined">
      <span
        v-if="solution.logoImageUrl"
        class="o-label__name--icon">
        <img
          class="img"
          :src="solution.logoImageUrl"
          loading="lazy"
          alt=""
          data-is-liquid="true">
      </span>
      <span
        v-if="solution.title"
        class="o-label__name--label is-solution">
        {{ solution.title }}
      </span>
      <span
        v-if="solution.company?.name"
        class="o-label__name--label is-company">
        {{ solution.company?.name }}
      </span>
    </component>
  <!-- /.o-label__name --></div>
</template>

<style lang="sass" scoped>
// object
.o-label__name
  $root: &
  $line-height: 1.4
  $icon-width: $line-height  // same size as publisher icon in ContentsCard.vue
  $icon-margin: 0.4em
  font-size: $font-x-small

  & &--item
    position: relative
    line-height: $line-height
    color: $font-light-gray

    &:has(> #{$root}--icon)
      padding-left: $icon-width + $icon-margin

    &[href]
      display: block
      color: $font-dark-blue
      text-decoration: none
      +transition

      +mouseover
        text-decoration: underline

    #{$root}--item
      color: inherit

  & &--icon
    width: #{$icon-width}em
    height: #{$line-height}em
    display: flex
    flex-direction: column
    justify-content: center
    position: absolute
    top: 0
    left: 0
    overflow: hidden

    > .img
      height: 100%
      display: block
      object-fit: contain
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

  & &--label
    &.is-solution
      &::after
        content: " | "

      &:last-child
        &::after
          display: none

  &.is-small
    font-size: $font-x-small

  &.is-normal
    font-size: $font-normal

  &.is-truncate
    #{$root}--item
      overflow: hidden
      text-overflow: ellipsis
      white-space: nowrap
</style>
